// begin frontend/src/components/ValidatedPhoneInput.tsx

import { CircularProgress } from '@mui/material';
import { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import { baseURL } from 'src/libs/axios';
import ErrorPopup from './ErrorPopup';
import { useAppDispatch } from 'src/store';
import { trackAnalytics } from 'src/features/analytics/analytics.slice';

const ValidatedPhoneInput = ({ value, setValue, setIsPhoneVerified, showValidateButton = false, setPhoneError = (_) => { }, existingUser = false }) => {
  const [code, setCode] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(value || "");
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState(null);
  const [showValidate, setShowValidate] = useState(showValidateButton)
  const dispatch = useAppDispatch()

  const handlePhoneValidationFail = (message) => {
    triggerError(message)
    setIsPhoneVerified(false);
    setPhoneError("Sorry! Could not verify Cell Phone Number");
    dispatch(trackAnalytics({ action_name: "user_phone_verification_failed", payload: { description: `user failed to verify phone number ${phoneNumber}`, failed_verification_phone: phoneNumber } }))
    setTimeout(() => {
      setPhoneError(null);
    }, 10000);
    restart()
  }

  const triggerError = (message) => {
    setError(message)
    setTimeout(() => {
      setError(null)
    }, 5000)
  }

  const onStart = async () => {
    console.warn('Validating', phoneNumber);
    setValidating(true);

    const params = new URLSearchParams();
    params.append('phone', `+${phoneNumber}`);
    if (existingUser) {
      params.append('for_login', 'true');
    }

    fetch(`${baseURL}/verify_phone/start?${params.toString()}`, {
      method: 'POST',
    }).then((response) => {
      return response.json();
    }).then((data) => {
      if (data.success) {
        setCode('');
        dispatch(trackAnalytics({ action_name: "user_phone_verification_started", payload: { description: `user started phone verification ${phoneNumber}`, verified_phone_number: phoneNumber } }))
        setValidating(false);
      } else {
        // Check if this is an existing user error
        const errorMessage = data.message || '';
        const isExistingUserError = errorMessage.includes('already associated with an existing account') ||
                                   errorMessage.includes('already registered');

        if (isExistingUserError) {
          // For login scenarios, existing user error is expected, so proceed
          if (existingUser) {
            // For login, we expect the user to exist, so proceed with the flow
            setCode('');
            setValidating(false);
            return;
          } else {
            // For registration, show error and redirect
            setPhoneError('User already exists. Please log in instead.');
            sessionStorage.setItem('loginError', 'User already exists. Please log in instead.');
            setValidating(false);
            return;
          }
        }

        triggerError(data.message)
        setValidating(false);
      }
    }).catch((error) => {
      triggerError(error.message)
      setValidating(false);
    });
  };

  const onCheck = async () => {
    console.warn('Validating', phoneNumber);
    setValidating(true);

    const params = new URLSearchParams();
    params.append('phone', `+${phoneNumber}`);
    params.append('code', code);
    if (existingUser) {
      params.append('for_login', 'true');
    }

    fetch(`${baseURL}/verify_phone/check?${params.toString()}`, {
      method: 'POST',
    }).then((response) => {
      return response.json();
    }).then((data) => {
      if (data.success) {
        // For login scenarios (existingUser === true), we want to proceed even if user exists
        if (existingUser) {
          // For login, we expect the user to exist, so proceed with verification
          setCode(true);
          setValue("existing_user", true);
          setValue("phone_verification", data.token);
          dispatch(trackAnalytics({ action_name: "user_phone_verification_completed", payload: { description: `user completed phone verification` } }))
          setIsPhoneVerified(true);
          setValidating(false);
        } else {
          // For registration scenarios, check if user already exists
          if (data.existing_user) {
            const reason = data.existing_user_reason;
            const existingEmail = data.existing_user_email;

            if (reason === 'phone_number') {
              setPhoneError(`Phone number +${phoneNumber} is already associated with account ${existingEmail}. Please log in instead.`);
            } else if (reason === 'email') {
              setPhoneError(`Email is already registered. Please log in instead.`);
            } else {
              setPhoneError('User already exists with this phone number or email. Please log in instead.');
            }

            // Store error for login page
            sessionStorage.setItem('loginError', 'User already exists. Please log in instead.');
            setValidating(false);
            return;
          }

          setCode(true);
          setValue("existing_user", false);
          setValue("phone_verification", data.token);
          dispatch(trackAnalytics({ action_name: "user_phone_verification_completed", payload: { description: `user completed phone verification` } }))
          setIsPhoneVerified(true);
          setValidating(false);
        }
      } else {
        handlePhoneValidationFail(data.message)
        setValidating(false);
      }
    }).catch((error) => {
      handlePhoneValidationFail(error.message)
      setValidating(false);
    });
  };

  const restart = () => {
    setCode(null)
    setValue("phone", '')
    setPhoneNumber('')
  };

  if (code === null) {
    return (
      <>
        {error && <ErrorPopup message={error} />}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
          <PhoneInput
            value={phoneNumber}
            enableSearch={true}
            onlyCountries={['us']}
            disableDropdown={true}
            country='us'
            placeholder='Enter phone number'
            inputProps={{
              id: 'phone',
              name: 'phone',
              className: 'ht-46 w-100 form-control',
            }}
            onChange={(e) => {
              if (!e.startsWith("1")) { e = "1" + e }
              setValue("phone", e);
              setValue("existing_user", false);
              setIsPhoneVerified(false);
              setPhoneNumber(e)
              setShowValidate(true)
            }}
          />

          <button
            disabled={validating}
            onClick={onStart}
            className='btn btn-lg btn-primary'
            style={{
              transition: '300ms',
              visibility: phoneNumber.length > 9 && showValidate ? 'visible' : 'hidden',
              opacity: phoneNumber.length > 9 && showValidate ? '1' : '0',
            }}
          >
            {validating ? <CircularProgress color="inherit" size="1rem" /> : <span className="fw-medium">Start verification</span>}
          </button>
        </div>
      </>
    );
  }

  if (code === true) {
    return <div>Phone number verified: +{phoneNumber}</div>;
  }

  return (
    <>
      {error && <ErrorPopup message={error} />}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
        Please enter the code sent to you via text.
        <input type='text' value={code} className='ht-46 w-50 form-control' onChange={(e) => setCode(e.target.value)} />
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
          <button onClick={restart} disabled={validating} className='btn btn-lg btn-dark' type='button'>
            <span className="fw-medium">Restart</span>
          </button>
          <button onClick={onCheck} disabled={validating} className='btn btn-lg btn-primary' type='button'>
            {validating ? <CircularProgress color="inherit" size="1rem" /> : <span className="fw-medium">Validate phone</span>}
          </button>
        </div>
      </div>
    </>
  );
};

export default ValidatedPhoneInput;


//end frontend/src/components/ValidatedPhoneInput.tsx
